import { useCallback, useEffect, useRef } from 'react'
import { post } from 'src/apis/apiService'
import config from 'src/config'

interface UseTrackProps {
  trackUrl?: string
  logEntryUrl?: string
  enable?: boolean
}

const HEARTBEAT_INTERVAL_MS = 30000
const MAX_CONSECUTIVE_AUTH_FAILURES = 3

export const useTrack = ({
  // /api/session-exit is the canonical exit endpoint (/api/track remains a
  // server-side alias for stale cached builds).
  trackUrl = `${config.apiUrl}/api/session-exit`,
  logEntryUrl = `${config.apiUrl}/api/log-entry`,
}: UseTrackProps) => {
  const heartbeatUrl = `${config.apiUrl}/api/heartbeat`

  // Watch-time span start. A ref (not a closure `let`) so the exit handler
  // can reset it and a later exit can never double-count the same span.
  const startTimeRef = useRef<number>(Date.now())
  // Exit dedup: beforeunload + visibilitychange:hidden + pagehide within one
  // teardown must produce exactly ONE beacon. Cleared when the tab becomes
  // visible again (a new span starts).
  const exitSentRef = useRef<boolean>(false)
  // Entry dedup: don't fire log_entry while one is already in flight.
  const entryInFlightRef = useRef<boolean>(false)
  // Heartbeat failure tracking: transient misses are counted, never reloaded.
  const consecutiveMissesRef = useRef<number>(0)
  const consecutiveAuthFailuresRef = useRef<number>(0)

  const resetSession = useCallback(() => {
    window.location.reload()
  }, [])

  // Heartbeat — also carries presence info so the server only records
  // auditorium presence for visible tabs.
  const sendHeartbeat = useCallback(() => {
    post(
      heartbeatUrl,
      () => {
        consecutiveMissesRef.current = 0
        consecutiveAuthFailuresRef.current = 0
      },
      (error: { response?: { status?: number } }) => {
        const status = error?.response?.status
        if (status === 401) {
          // Only an explicit auth failure, three ticks in a row, forces the
          // reload/reset path.
          consecutiveAuthFailuresRef.current += 1
          if (consecutiveAuthFailuresRef.current >= MAX_CONSECUTIVE_AUTH_FAILURES) {
            resetSession()
          }
          return
        }
        // Network error or 5xx (e.g. a deploy): count the miss and carry on.
        // NEVER reload for these — a single 502 must not reload every viewer.
        consecutiveAuthFailuresRef.current = 0
        consecutiveMissesRef.current += 1
        console.warn(`Heartbeat miss #${consecutiveMissesRef.current}:`, error)
      },
      {
        page: 'auditorium',
        visible: document.visibilityState === 'visible',
      }
    )
  }, [heartbeatUrl, resetSession])

  useEffect(() => {
    const checkHeartbeat = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)

    return () => {
      clearInterval(checkHeartbeat)
    }
  }, [sendHeartbeat])

  // Log Entry Time
  const log_entry = useCallback(() => {
    if (entryInFlightRef.current) return
    entryInFlightRef.current = true
    post(
      logEntryUrl,
      () => {
        entryInFlightRef.current = false
      },
      (error: unknown) => {
        entryInFlightRef.current = false
        console.error(error)
      }
    )
  }, [logEntryUrl])

  // Time Duration Tracking and Log Exit
  const sendExitBeacon = useCallback(() => {
    if (exitSentRef.current) return
    exitSentRef.current = true

    const timeSpent = Date.now() - startTimeRef.current
    const jwtToken = localStorage.getItem('jwt_token')
    const body = JSON.stringify({ timeSpent, jwtToken })
    // 'text/plain' keeps the beacon a CORS "simple request" — an
    // 'application/json' Blob forces a preflight that dies during page
    // teardown. The server parses raw_post, so the content type is irrelevant.
    const blob = new Blob([body], { type: 'text/plain; charset=utf-8' })

    let queued = false
    if (typeof navigator.sendBeacon === 'function') {
      try {
        queued = navigator.sendBeacon(trackUrl, blob)
      } catch {
        queued = false
      }
    }
    if (!queued) {
      fetch(trackUrl, { method: 'POST', body, keepalive: true }).catch(() => {})
    }

    // Reset the span start immediately so a subsequent exit can never
    // double-count the time already reported.
    startTimeRef.current = Date.now()
  }, [trackUrl])

  useEffect(() => {
    startTimeRef.current = Date.now()
    exitSentRef.current = false

    log_entry()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendExitBeacon()
      }

      if (document.visibilityState === 'visible') {
        exitSentRef.current = false
        startTimeRef.current = Date.now()
        log_entry()
      }
    }

    const handlePageExit = () => {
      sendExitBeacon()
    }

    let stayTimer: ReturnType<typeof setTimeout>

    const handleBeforeUnload = () => {
      sendExitBeacon()
      // beforeunload is no longer a reliable teardown signal: PlenaryLeaveGuard
      // raises the browser's native "Leave site?" dialog on this same event, so
      // the participant can still pick Cancel and stay in the hall. The dialog
      // blocks the event loop, so this timer can only ever fire once the page
      // has survived — i.e. exactly in that Cancel case. Reopen the span there,
      // otherwise exitSentRef stays true (visibilitychange never fires for a
      // beforeunload dialog) and every second of the rest of the visit is
      // dropped. sendExitBeacon already reset startTimeRef, so reopening cannot
      // double-count the time just reported.
      clearTimeout(stayTimer)
      stayTimer = setTimeout(() => {
        exitSentRef.current = false
        startTimeRef.current = Date.now()
      }, 1000)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
    // pagehide is the reliable teardown signal on mobile Safari/Chrome where
    // beforeunload often never fires. Nothing can cancel it, so no reopen here.
    window.addEventListener('pagehide', handlePageExit)

    return () => {
      clearTimeout(stayTimer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageExit)
      sendExitBeacon()
    }
    // Mount/unmount only: entry on mount, exit beacon on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
